// Authoring schemas may contain numbers outside IEEE-754. Keep their lexemes
// through reads, edits and retries; the ordinary Console transport uses Number.
export class ExactNumber {
  constructor(value) { this.value = value; Object.freeze(this); }
  toString() { return this.value; }
}

export function decimal(value) {
  const match = String(value).match(/^(-?)(0|[1-9]\d*)(?:\.(\d+))?(?:[eE]([+-]?\d+))?$/);
  if (!match) throw new Error("Введите число");
  let digits = (match[2] + (match[3] || "")).replace(/^0+/, "");
  if (!digits) return "0";
  if (digits.length > 10000 || (match[4] || "").length > 7) throw new Error("Число превышает допустимый размер");
  let exponent = BigInt(match[4] || 0) - BigInt((match[3] || "").length);
  const trailing = digits.length - digits.replace(/0+$/, "").length;
  digits = digits.slice(0, digits.length - trailing);
  exponent += BigInt(trailing);
  if (exponent < -10000n || exponent > 10000n) throw new Error("Число превышает допустимый размер");
  return match[1] + digits + (exponent ? `e${exponent}` : "");
}

export function exactNumber(value) {
  const canonical = decimal(value);
  const number = Number(value);
  return Number.isFinite(number) && decimal(String(number)) === canonical ? number : new ExactNumber(String(value));
}

export function compareNumbers(left, right) {
  const parts = (value) => { const [coefficient, exponent = "0"] = decimal(String(value)).split("e"); return [BigInt(coefficient), Number(exponent)]; };
  const [a, x] = parts(left), [b, y] = parts(right), power = Math.min(x, y);
  const difference = a * 10n ** BigInt(x - power) - b * 10n ** BigInt(y - power);
  return difference < 0n ? -1 : difference > 0n ? 1 : 0;
}

export function parseAuthoringJSON(text) {
  let index = 0;
  const fail = () => { throw new Error("Некорректный JSON конструктора"); };
  const space = () => { while (/[\x20\t\r\n]/.test(text[index] || "x")) index++; };
  const string = () => {
    const start = index++;
    while (index < text.length) {
      const char = text[index++];
      if (char === "\\") index++;
      else if (char === '"') return JSON.parse(text.slice(start, index));
    }
    fail();
  };
  function read(depth = 0) {
    if (depth > 100) fail();
    space();
    const char = text[index];
    if (char === '"') return string();
    if (char === "{" || char === "[") {
      index++; space();
      const object = char === "{";
      const end = object ? "}" : "]";
      const entries = [], keys = new Set();
      if (text[index] === end) { index++; return object ? {} : []; }
      while (index < text.length) {
        space();
        if (object) {
          if (text[index] !== '"') fail();
          const key = string(); space();
          if (keys.has(key) || text[index++] !== ":") fail();
          keys.add(key); entries.push([key, read(depth + 1)]);
        } else entries.push(read(depth + 1));
        space();
        if (text[index] === end) { index++; return object ? Object.fromEntries(entries) : entries; }
        if (text[index++] !== ",") fail();
      }
      fail();
    }
    for (const [token, value] of [["true", true], ["false", false], ["null", null]]) {
      if (text.startsWith(token, index)) { index += token.length; return value; }
    }
    const number = text.slice(index).match(/^-?(?:0|[1-9]\d*)(?:\.\d+)?(?:[eE][+-]?\d+)?/);
    if (!number) fail();
    index += number[0].length;
    return exactNumber(number[0]);
  }
  const value = read(); space();
  if (index !== text.length) fail();
  return value;
}

const quote = (value) => JSON.stringify(value).replace(/[<>&\u2028\u2029]/g, (char) => `\\u${char.charCodeAt(0).toString(16).padStart(4, "0")}`);
const utf8Order = (a, b) => {
  const x = new TextEncoder().encode(a), y = new TextEncoder().encode(b);
  for (let i = 0; i < Math.min(x.length, y.length); i++) if (x[i] !== y[i]) return x[i] - y[i];
  return x.length - y.length;
};
export function stringifyAuthoringJSON(value) {
  if (value instanceof ExactNumber || typeof value === "number") return decimal(String(value));
  if (value === null) return "null";
  if (typeof value === "string") return quote(value);
  if (typeof value === "boolean") return String(value);
  if (Array.isArray(value)) return `[${value.map(stringifyAuthoringJSON).join(",")}]`;
  if (typeof value !== "object") throw new Error("Неподдержанное значение конструктора");
  return `{${Object.keys(value).sort(utf8Order).map((key) => `${quote(key)}:${stringifyAuthoringJSON(value[key])}`).join(",")}}`;
}
export const cloneAuthoring = (value) => parseAuthoringJSON(stringifyAuthoringJSON(value));
export const authoringRequest = (api, path, options = {}) => api.request(path, {
  ...options, parseJSON: parseAuthoringJSON, stringifyJSON: stringifyAuthoringJSON,
});
